/**
 * Clinic Heat Map (#staff) — fleet-wide patient-coverage at a glance.
 *
 * One screen: does every clinic have patients booked for the days ahead? Each
 * clinic is a today + 7-day runway; empty days are real booking gaps, sync
 * issues mean the pull broke or never ran. Clinics that need action sort to the
 * top automatically. Counts-only v1 (weekends shown, never muted) — the
 * open-days signal sharpens it later.
 *
 * Renders real data via `listClinicCoverageForStaff`. v1 manual-refresh.
 */

import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import {
  listClinicCoverage,
  type ClinicCoverageResult,
  type ClinicCoverageRow,
  type ClinicCoverageStatus,
} from '../services/clinicCoverage'
import './ClinicHeatMap.css'

type ChipFilter = 'all' | ClinicCoverageStatus

const CHIPS: { key: ChipFilter; label: string; dot?: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'attention', label: 'Needs attention', dot: 'chm-dot--attention' },
  { key: 'sync', label: 'Sync issues', dot: 'chm-dot--sync' },
  { key: 'healthy', label: 'Healthy', dot: 'chm-dot--healthy' },
  { key: 'new', label: 'New practices', dot: 'chm-dot--new' },
]

const HINTS: Record<ChipFilter, string> = {
  all: '',
  attention: 'Clinics with an empty or thin day on the schedule. These need patients booked.',
  sync: 'Clinics where the data pull failed or never ran. A pipeline problem, not a booking problem.',
  healthy: 'Clinics with every pulled day well booked. Nothing to do here.',
  new: 'Practices set up in the last 72 hours. Still ramping, empty days expected.',
}

function fmtUpdated(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

function Cell({ state, count, note, onFail }: { state: string; count?: number; note?: string; onFail?: () => void }) {
  if (state === 'healthy' || state === 'low')
    return <div className={`chm-tile chm-tile--${state}`}>{count}</div>
  if (state === 'empty') return <div className="chm-tile chm-tile--empty" title="No patients booked">0</div>
  if (state === 'closed') return <div className="chm-tile chm-tile--closed" title="Clinic closed this day (operating-days setting)">Closed</div>
  if (state === 'syncerror')
    return (
      <button
        type="button"
        className="chm-tile chm-tile--fail"
        title={`${note ? note + ' — ' : ''}click to see the failed pulls`}
        onClick={onFail}
      >Failed</button>
    )
  // In-flight pull: show the last-known count (carried on the run doc) with a
  // pulsing dot so a re-pull reads as "refreshing", not a lost-patients gap.
  if (state === 'pending')
    return count != null && count > 0
      ? (
        <div className="chm-tile chm-tile--pulling" title={`Pull in progress — last known: ${count} patient${count === 1 ? '' : 's'}`}>
          <span className="chm-pulse" />{count}
        </div>
      )
      : <div className="chm-tile chm-tile--pulling chm-tile--pulling-new" title="Pull in progress"><span className="chm-pulse" />…</div>
  return <div className="chm-tile chm-tile--none" title="no pull yet" />
}

export default function ClinicHeatMap(
  { onOpenFailed }: { onOpenFailed?: (row: ClinicCoverageRow, dayIso: string | null) => void } = {},
) {
  const [data, setData] = useState<ClinicCoverageResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<ChipFilter>('all')
  const [query, setQuery] = useState('')

  function refresh() {
    setLoading(true)
    setError(null)
    listClinicCoverage()
      .then((res) => { setData(res); setLoading(false) })
      .catch((err) => {
        Sentry.captureException(err)
        setError(err instanceof Error ? err.message : 'Failed to load clinic coverage.')
        setLoading(false)
      })
  }

  // Fetch once on mount. State is synced from the Cloud Function.
  // eslint-disable-next-line react-hooks/set-state-in-effect -- the one fetch on mount; refresh sets the loading state as it starts
  useEffect(() => { refresh() }, [])

  const clinics = useMemo(() => data?.clinics ?? [], [data])
  const counts = useMemo(() => {
    const c: Record<ChipFilter, number> = { all: clinics.length, attention: 0, sync: 0, healthy: 0, new: 0 }
    clinics.forEach((r) => { c[r.status]++ })
    return c
  }, [clinics])

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return clinics.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false
      if (q && !(`${r.name} ${r.org} ${r.region} ${r.mainLogin ?? ''}`.toLowerCase().includes(q))) return false
      return true
    })
  }, [clinics, filter, query])

  const days = data?.days ?? []

  return (
    <div className="chm">
      <div className="chm-head">
        <div>
          <h1>Clinic Heat Map</h1>
          <div className="chm-subt">
            Patients booked per clinic for the days ahead · internal ops
            {data && <> · last updated {fmtUpdated(data.generatedAt)}</>}
          </div>
        </div>
        <button className="chm-refresh" onClick={refresh} disabled={loading}>↻ {loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      <div className="chm-toolbar">
        <div className="chm-chips">
          {CHIPS.map((chip) => (
            <button
              key={chip.key}
              className={`chm-chip${filter === chip.key ? ' chm-chip--on' : ''}`}
              onClick={() => setFilter(chip.key)}
            >
              {chip.dot && <span className={`chm-dot ${chip.dot}`} />}
              {chip.label} <span className="chm-c">{counts[chip.key]}</span>
            </button>
          ))}
        </div>
        <input
          className="chm-search"
          type="search"
          placeholder="Search clinic, org, or city…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search clinics"
        />
      </div>

      <div className="chm-hint">{HINTS[filter]}</div>

      <div className="chm-legend">
        <span className="chm-sw"><i className="chm-b chm-b--healthy" /> Healthy (8+ patients)</span>
        <span className="chm-sw"><i className="chm-b chm-b--low" /> Low (1–7)</span>
        <span className="chm-sw"><i className="chm-b chm-b--empty" /> Empty — booking gap</span>
        <span className="chm-sw"><i className="chm-b chm-b--sync" /> Pull failed</span>
        <span className="chm-sw"><i className="chm-b chm-b--pulling" /> Pulling… (refreshing)</span>
        <span className="chm-sw"><i className="chm-b chm-b--nodata" /> No pull yet</span>
        <span className="chm-sw"><i className="chm-b chm-b--closed" /> Closed day</span>
      </div>

      {error && <p className="chm-error">{error}</p>}
      {loading && !data && <p className="chm-loading">Loading clinic coverage…</p>}

      {data && (
        <div className="chm-board">
          <div className="chm-grid chm-axis">
            <div className="chm-corner">Clinic</div>
            {days.map((d) => (
              <div key={d.iso} className={`chm-col${d.weekend ? ' chm-col--wknd' : ''}${d.today ? ' chm-col--today' : ''}`}>
                <span className="chm-dow">{d.dow}</span>
                <span className="chm-dt">{d.iso.slice(5)}</span>
              </div>
            ))}
          </div>

          {shown.map((r: ClinicCoverageRow) => (
            <div key={`${r.orgId}/${r.clinicId}`} className="chm-grid chm-lane">
              <div className={`chm-clinic${r.status === 'attention' ? ' chm-clinic--flag' : r.status === 'sync' ? ' chm-clinic--sync' : ''}`}>
                <div className="chm-name">
                  {r.name}
                  {r.status === 'new' && <span className="chm-newtag">NEW</span>}
                </div>
                <div className="chm-region">{r.org}{r.region ? ` · ${r.region}` : ''}</div>
                {r.mainLogin && <div className="chm-login" title="Main login (practice admin)">{r.mainLogin}</div>}
              </div>
              {r.cells.map((cell, i) => (
                <div key={i} className={`chm-slot${days[i]?.weekend ? ' chm-slot--wknd' : ''}${days[i]?.today ? ' chm-slot--today' : ''}`}>
                  <Cell state={cell.state} count={cell.count} note={cell.note} onFail={() => onOpenFailed?.(r, days[i]?.iso ?? null)} />
                </div>
              ))}
            </div>
          ))}

          {shown.length === 0 && <p className="chm-empty">No clinics match this filter.</p>}
        </div>
      )}

      <div className="chm-foot">
        Empty days are real booking gaps; sync issues mean the pull failed or never ran (pipeline, not
        booking). A pulsing tile means a pull is in progress — the night-before sweep re-pulls tomorrow
        each evening, so a refreshing day keeps its last-known count, it isn&rsquo;t a lost day. Closed days
        come from each clinic&rsquo;s operating-days setting (clinic pop-up) — clinics without the setting
        are judged on data alone, weekends included.
      </div>
    </div>
  )
}
